import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Col, type ColProps, Space, Table } from 'antd';
import { useFormikContext } from 'formik';
import { get } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DbsPayloadScheme } from '../../../../api/types';
import ArrayField from '../../../../components/fields/ArrayField';
import FormItem from '../../../../components/fields/FormItem';
import InputField from '../../../../components/fields/InputField';
import InputNumberField from '../../../../components/fields/InputNumberField';
import SelectField from '../../../../components/fields/SelectField';
import SingleSecretUploadField from '../../../../components/fields/SingleSecretUploadField';
import ResponsiveStyledTable from '../../../shared/ResponsiveStyledTable';

const defaultColSpan = { lg: 8, md: 12, xs: 24 };

export type DbsBankIntegrationProps = {
    prefix?: string;
    colSpan?: ColProps;
    disabled?: boolean;
};

const fieldNames = [
    'allowedIps',
    'host',
    'orgId',
    'apiKey',
    'privateKey',
    'publicCert',
    'certCA',
    'mappings',
    'payloadScheme',
    'dealerCode',
] as const;

const useDbsPayloadSchemeOptions = () => {
    const { t } = useTranslation(['bankDetails']);

    return useMemo(
        () => [
            { value: DbsPayloadScheme.V1, label: t('bankDetails:options.dbsPayloadScheme.v1') },
            { value: DbsPayloadScheme.V2, label: t('bankDetails:options.dbsPayloadScheme.v2') },
        ],
        [t]
    );
};

const DbsBankIntegration = ({ prefix, colSpan = defaultColSpan, disabled = false }: DbsBankIntegrationProps) => {
    const { t } = useTranslation(['bankDetails']);

    const names = useMemo(
        () =>
            Object.fromEntries(fieldNames.map(name => [name, prefix ? `${prefix}.${name}` : name])) as Record<
                (typeof fieldNames)[number],
                string
            >,
        [prefix]
    );

    const { values } = useFormikContext();
    const mappings = useMemo(() => get(names.mappings, values) ?? [], [values, names.mappings]);
    const payloadSchemeOptions = useDbsPayloadSchemeOptions();

    return (
        <>
            <Col {...colSpan}>
                <InputField
                    {...t('bankDetails:fields.bankIntegration.allowedIps', { returnObjects: true })}
                    disabled={disabled}
                    name={names.allowedIps}
                    required
                />
            </Col>
            <Col {...colSpan}>
                <InputField
                    {...t('bankDetails:fields.dbsIntegration.host', { returnObjects: true })}
                    addonBefore="https://"
                    disabled={disabled}
                    name={names.host}
                    required
                />
            </Col>
            <Col {...colSpan}>
                <InputField
                    {...t('bankDetails:fields.dbsIntegration.orgId', { returnObjects: true })}
                    disabled={disabled}
                    name={names.orgId}
                    required
                />
            </Col>
            <Col {...colSpan}>
                <InputField
                    {...t('bankDetails:fields.dbsIntegration.apiKey', { returnObjects: true })}
                    disabled={disabled}
                    name={names.apiKey}
                    required
                />
            </Col>
            <Col {...colSpan}>
                <SingleSecretUploadField
                    {...t('bankDetails:fields.dbsIntegration.privateKey', { returnObjects: true })}
                    disabled={disabled}
                    name={names.privateKey}
                    required
                />
            </Col>
            <Col {...colSpan}>
                <SingleSecretUploadField
                    {...t('bankDetails:fields.dbsIntegration.publicCert', { returnObjects: true })}
                    disabled={disabled}
                    name={names.publicCert}
                    required
                />
            </Col>
            <Col {...colSpan}>
                <SingleSecretUploadField
                    {...t('bankDetails:fields.dbsIntegration.certCA', { returnObjects: true })}
                    disabled={disabled}
                    name={names.certCA}
                />
            </Col>
            <Col {...colSpan}>
                <SelectField
                    {...t('bankDetails:fields.dbsIntegration.payloadScheme', { returnObjects: true })}
                    name={names.payloadScheme}
                    options={payloadSchemeOptions}
                    required
                />
            </Col>
            <Col {...colSpan}>
                <InputField
                    {...t('bankDetails:fields.bankIntegration.dealerCodeForAudiAndSDM', { returnObjects: true })}
                    disabled={disabled}
                    name={names.dealerCode}
                />
            </Col>
            <Col lg={24} md={24} sm={24} xs={24}>
                <ArrayField
                    name={names.mappings}
                    render={({ push, remove }) => (
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <ResponsiveStyledTable
                                dataSource={mappings}
                                pagination={false}
                                rowKey={(record, index) => index.toString()}
                            >
                                <Table.Column
                                    dataIndex="appliedInterestRate"
                                    render={(_, __, rowIndex) => (
                                        <InputNumberField
                                            disabled={disabled}
                                            name={`${names.mappings}[${rowIndex}].appliedInterestRate`}
                                        />
                                    )}
                                    title={
                                        <FormItem
                                            label={t('bankDetails:fields.dbsIntegration.appliedInterestRate.label')}
                                            required
                                        />
                                    }
                                />
                                <Table.Column
                                    dataIndex="schemeCode"
                                    render={(_, __, rowIndex) => (
                                        <InputField
                                            disabled={disabled}
                                            name={`${names.mappings}[${rowIndex}].schemeCode`}
                                        />
                                    )}
                                    title={
                                        <FormItem
                                            label={t('bankDetails:fields.dbsIntegration.schemeCode.label')}
                                            required
                                        />
                                    }
                                />
                                <Table.Column
                                    dataIndex="packageCode"
                                    render={(_, __, rowIndex) => (
                                        <InputField
                                            disabled={disabled}
                                            name={`${names.mappings}[${rowIndex}].packageCode`}
                                        />
                                    )}
                                    title={
                                        <FormItem
                                            label={t('bankDetails:fields.dbsIntegration.packageCode.label')}
                                            required
                                        />
                                    }
                                />
                                <Table.Column
                                    render={(_, __, rowIndex) =>
                                        !disabled && (
                                            <Button
                                                icon={<DeleteOutlined />}
                                                onClick={() => remove(rowIndex)}
                                                size="small"
                                                style={{ marginBottom: 16 }}
                                                type="link"
                                                danger
                                            />
                                        )
                                    }
                                    width={60}
                                />
                            </ResponsiveStyledTable>
                            {!disabled && (
                                <Button
                                    icon={<PlusOutlined />}
                                    onClick={() => push({ appliedInterestRate: 0, schemeCode: '', packageCode: '' })}
                                    type="primary"
                                >
                                    {t('bankDetails:actions.addMapping')}
                                </Button>
                            )}
                        </Space>
                    )}
                />
            </Col>
        </>
    );
};

export default DbsBankIntegration;
